import {
  PageContainer,
  ProForm,
  ProFormDateTimePicker,
  ProFormSwitch,
  ProFormText,
} from "@ant-design/pro-components";
import { useNavigate, useParams } from "react-router-dom";
import { LeagueGamesAPI } from "../../../api/leagueGames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getClubs } from "../../../api";
import { useForm } from "antd/es/form/Form";
import * as dayjs from "dayjs";
import { Avatar, Button, Input, notification, Table, Tag } from "antd";
import { SortingText } from "../../../utils/sortingData";

const EditLeagueGame = () => {
  const navigate = useNavigate();

  const { id } = useParams();
  const { editLeagueGame, getDetailLeagueGames } = LeagueGamesAPI;

  const [form] = useForm();

  const [keyWordSearch, setKeyWordSearch] = useState(null);
  const [searchClub, setSearchClub] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [clubsSelected, setClubsSelected] = useState({});
  const [clubs, setClubs] = useState([]);
  const [totalItems, setTotalItems] = useState(null);
  const [clubAssigned, setClubAssigned] = useState({});

  const fetchClubs = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await getClubs({ keyword: searchClub }, currentPage, 10);

      setClubs(response.results);
      setTotalItems(response.total);
    } catch (error) {
      console.log("Error", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchClub]);

  const handleSubmit = async (values) => {
    console.log("🚀 ===== values:", values);

    try {
      const result = {
        nameAr: values.nameAr,
        nameEn: values.nameEn,
        startTime: dayjs
          .tz(values.startTime, "YYYY-MM-DD HH:mm", "Asia/Riyadh")
          .utc()
          .format(),
        endTime: values.endTime
          ? dayjs
              .tz(values.endTime, "YYYY-MM-DD HH:mm", "Asia/Riyadh")
              .utc()
              .format()
          : null,
        isEnable: values.isEnable,
        clubs: [],
      };

      Object.keys(values).forEach((key) => {
        if (key.startsWith("isEnableClub-")) {
          const clubId = key.replace("isEnableClub-", "");

          result.clubs.push({
            clubId,
            metadata: {
              isEnable: values[key],
            },
          });
        }
      });

      await editLeagueGame(id, result);

      navigate("/league-game");
    } catch (error) {
      console.log("🚀 ===== error:", error);
    }
  };

  const handleFetchDetail = useCallback(async () => {
    try {
      const { data } = await getDetailLeagueGames(id);

      const formatted = data.LeagueClub.reduce((acc, item, index) => {
        const parsedMetadata = item.metadata ? JSON.parse(item.metadata) : {};

        acc[`isEnableClub-${item.clubId}`] = !!parsedMetadata.isEnable;

        return acc;
      }, {});

      form.setFieldsValue({ ...data, ...formatted });

      const result = {};

      data.LeagueClub.forEach((item) => {
        result[item.clubId] = {
          id: item.clubId,
          nameEn: item.club.nameEn,
        };
      });

      setClubAssigned(result);
    } catch (error) {
      console.log("🚀 ===== error:", error);
      notification.error({ message: "Error" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmitSearch = () => {
    setSearchClub(keyWordSearch);
    setCurrentPage(1);
  };

  const onSelect = useCallback((item, isSelected) => {
    setClubsSelected((preValue) => {
      const result = JSON.parse(JSON.stringify(preValue ?? {}));
      if (isSelected) {
        result[item.id] = item;
      } else {
        delete result[item.id];
      }

      return result;
    });
  }, []);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: Object.keys(clubsSelected),
      onSelect: onSelect,
    }),
    [clubsSelected, onSelect]
  );

  const tableColumns = [
    {
      key: "logo",
      title: "Logo",
      dataIndex: "logo",
      search: false,
      render: (_, record) => {
        return <Avatar src={record?.logo} />;
      },
    },
    {
      key: "nameEn",
      title: "English Name",
      dataIndex: "nameEn",
      search: false,
      sorter: (current, next) => SortingText(current.nameEn, next.nameEn),
    },
    {
      key: "nameAr",
      title: "Arabic Name",
      dataIndex: "nameAr",
      search: false,
      sorter: (current, next) => SortingText(current.nameAr, next.nameAr),
    },
  ];

  const handleDeleteClubAssigned = (clubId) => {
    console.log("🚀 ===== clubId:", clubId);
    setClubAssigned((prev) => {
      const updated = { ...prev };
      delete updated[clubId];

      return updated;
    });

    form.setFieldsValue({
      [`isEnableClub-${clubId}`]: undefined,
    });
  };

  useEffect(() => {
    fetchClubs();
  }, [fetchClubs]);

  useEffect(() => {
    handleFetchDetail();
  }, [handleFetchDetail]);

  return (
    <PageContainer title="Create League Game">
      <ProForm
        form={form}
        name="settingForm"
        layout="horizontal"
        onFinish={handleSubmit}
      >
        <ProForm.Group>
          <div className="form-fields-wrapper">
            <ProFormText
              width="lg"
              label="Name AR"
              placeholder="Enter name AR"
              name="nameAr"
              rules={[
                {
                  required: true,
                  message: () => (
                    <p className="error-message">
                      Name AR is required! My friends
                    </p>
                  ),
                },
              ]}
            />
            <ProFormText
              width="lg"
              label="Name EN"
              placeholder="Enter name EN"
              name="nameEn"
              rules={[
                {
                  required: true,
                  message: () => (
                    <p className="error-message">
                      Name EN is required! My friends
                    </p>
                  ),
                },
              ]}
            />
            <ProFormDateTimePicker
              initialValue={new Date()}
              name="startTime"
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              width="lg"
              label="Start time"
              placeholder="Choose start time"
              rules={[
                {
                  required: true,
                  message: () => (
                    <p className="error-message">
                      Start Time is required! My friends
                    </p>
                  ),
                },
              ]}
            />
            <ProFormDateTimePicker
              initialValue={null}
              name="endTime"
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              width="lg"
              label="End time"
              placeholder="Choose end time"
            />
            <ProFormSwitch name="isEnable" label="League Game Toggle" />
          </div>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <p>Clubs Assigned: </p>
              <div>
                {Object.values(clubAssigned).map((club) => (
                  <Tag
                    key={club.id}
                    closeIcon
                    onClose={() => handleDeleteClubAssigned(club.id)}
                  >
                    {club.nameEn}
                  </Tag>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <Input
                style={{ marginBottom: "10px", width: "200px" }}
                placeholder="Search club"
                onChange={(e) => setKeyWordSearch(e.target.value)}
              />
              <Button htmlType="button" onClick={handleSubmitSearch}>
                Query
              </Button>
            </div>
            <Table
              loading={isLoading}
              rowSelection={rowSelection}
              size="small"
              style={{ marginBottom: "16px" }}
              rowKey="id"
              scroll={{ x: 1400, y: 500 }}
              columns={tableColumns}
              dataSource={clubs}
              pagination={{
                pageSize: 10,
                total: totalItems,
                onChange: (page) => setCurrentPage(page),
              }}
            />
          </div>

          {Array.from(
            new Map(
              [
                ...Object.values(clubAssigned),
                ...Object.values(clubsSelected),
              ].map((club) => [club.id, club])
            ).values()
          ).map((club) => (
            <ProFormSwitch
              key={club.id}
              name={`isEnableClub-${club.id}`}
              label={`Club ${club.nameEn} Enable`}
              initialValue={false}
            />
          ))}
        </ProForm.Group>
      </ProForm>
    </PageContainer>
  );
};

export default EditLeagueGame;
